import {useState, useEffect, useContext, useCallback} from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {Link} from 'react-router'

import DesktopNavEverplansBrandedCobranding from '../../../../shared_components/everplans_branded_cobranding/DesktopNavEverplansBrandedCobranding'
import NavCategories from './NavCategories'
import NavLinksController from './NavLinksController'
import SecureSharingItems from './SecureSharingItems'
import {GlobalAnnouncementContext} from '../../../../shared_components/ApplicationContainer'

import {hasEverplansCobranding} from '../../../../lib/cobrandingTools'

import {noop} from '../../../../lib/tools'
import {displayBanner} from '../../../../lib/bannerTools'

import './nav.scss'

/**
 * @todo Make the path for the dashboard better somehow...
 */


export const Nav = ({
  cobranding,
  currentUser,
  loadingCurrentUser,
  params,
  router = {isActive: noop, listenBefore: noop}
}) => {
  const [showDropdownNav, setShowDropdownNav] = useState(false)
  const [showSecureSharing, setShowSecureSharing] = useState(false)
  const globalAnnouncement = useContext(GlobalAnnouncementContext)

  useEffect(() => {
    const unlisten = router.listenBefore ?
      router.listenBefore(() => {
        setShowDropdownNav(false)
        setShowSecureSharing(false)
      }) :
      null

    return () => {
      if (typeof unlisten === 'function') unlisten()
      setShowDropdownNav(false)
      setShowSecureSharing(false)
    }
  }, [router])

  const navGroupClassNames = useCallback((navGroupType, activeNavGroupCondition) => (
    classnames(
      'nav-group',
      `nav-group-${navGroupType}`,
      {'active-nav-group': activeNavGroupCondition}
    )
  ), [])

  const isRouteActive = useCallback((cmRoute, exactPath = false) => (
    router.isActive(cmRoute, exactPath)
  ), [router])

  const dashboardClassNames = useCallback(() => {
    const activeNavGroupCondition = (
      isRouteActive('cm/dashboard') &&
      !(showDropdownNav || showSecureSharing)
    )
    return navGroupClassNames('link', activeNavGroupCondition)
  }, [isRouteActive, showDropdownNav, showSecureSharing, navGroupClassNames])

  const myPlanClassNames = useCallback(() => {
    const activeNavGroupCondition = (
      !showSecureSharing &&
      (showDropdownNav || router.isActive(`cm/category/${params?.slug}`))
    )
    return navGroupClassNames('row', activeNavGroupCondition)
  }, [showSecureSharing, showDropdownNav, router, params, navGroupClassNames])

  const addressBookClassName = useCallback(() => {
    if (router.isActive('cm/address-book')) return 'active-address-book-link'
  }, [router])

  const secureSharingClassNames = useCallback(() => {
    const activeNavGroupCondition = (
      !showDropdownNav &&
      (showSecureSharing || isRouteActive('cm/deputies') || isRouteActive('cm/deputy-for'))
    )
    return navGroupClassNames('secured-sharing', activeNavGroupCondition)
  }, [showDropdownNav, showSecureSharing, isRouteActive, navGroupClassNames])

  const updateWhichNavGroupIsActive = useCallback(sectionToDefinitelyToggle => {
    const newState = {
      showDropdownNav: false,
      showSecureSharing: false
    }
    if (['showDropdownNav', 'showSecureSharing'].includes(sectionToDefinitelyToggle))
      newState[sectionToDefinitelyToggle] = !(sectionToDefinitelyToggle === 'showDropdownNav' ? showDropdownNav : showSecureSharing)

    setShowDropdownNav(newState.showDropdownNav)
    setShowSecureSharing(newState.showSecureSharing)
  }, [showDropdownNav, showSecureSharing])

  const toggleNavMenu = useCallback(() => updateWhichNavGroupIsActive('showDropdownNav'), [updateWhichNavGroupIsActive])
  const toggleSecureSharing = useCallback(() => updateWhichNavGroupIsActive('showSecureSharing'), [updateWhichNavGroupIsActive])

  const secureSharingProps = useCallback(() => ({
    currentUser,
    deputiesOthersActive: isRouteActive('cm/deputy-for'),
    myDeputiesActive: isRouteActive('cm/deputies', true)
  }), [currentUser, isRouteActive])

  const onClickDashboardLink = useCallback(() => {
    if (showDropdownNav || showSecureSharing) updateWhichNavGroupIsActive()
  }, [showDropdownNav, showSecureSharing, updateWhichNavGroupIsActive])

  if (loadingCurrentUser) return null

  const displayBannerText = displayBanner({
    currentUser,
    globalAnnouncementText: globalAnnouncement.globalAnnouncementText
  })

  return (
    <nav className={classnames('nav flex-child', displayBannerText)}>
      {
        hasEverplansCobranding(cobranding) &&
        <DesktopNavEverplansBrandedCobranding cobranding={cobranding} />
      }
      <ul className='nav-groups flex-container'>
        <li className={dashboardClassNames()} onClick={onClickDashboardLink}>
          <Link className='nav-group-name nav-dashboard' to='cm/dashboard'>
            <p>Dashboard</p>
          </Link>
        </li>
        <li className={myPlanClassNames()}>
          <span>
            <div className='nav-menu'>
              <span className='nav-group-name nav-group-my-plan' onClick={toggleNavMenu}>My Everplan</span>
            </div>
            <NavCategories
              className='nav-categories-desktop'
              params={params}
              router={router}
            />
            <NavLinksController
              className={addressBookClassName()}
              currentUser={currentUser}
              showDropdownNav={showDropdownNav}
            />
          </span>
        </li>
        <li className={secureSharingClassNames()}>
          <span className='nav-group-name' onClick={toggleSecureSharing}>Sharing</span>
          <SecureSharingItems {...secureSharingProps()} className='secure-sharing-desktop' />
        </li>
      </ul>
      <NavLinksController
        className={addressBookClassName()}
        currentUser={currentUser}
        mobile
        showDropdownNav={showDropdownNav}
      />
      <NavCategories
        className={classnames('nav-categories-mobile', {'show-dropdown-nav': showDropdownNav, 'cm-layout': true})}
        params={params}
        router={router}
      />
      <SecureSharingItems
        {...secureSharingProps()}
        className={classnames('secure-sharing-mobile', {'show-dropdown-nav': showSecureSharing})}
      />
    </nav>
  )
}

Nav.propTypes = {
  cobranding: PropTypes.object,
  currentUser: PropTypes.shape({
    has_invited_deputy: PropTypes.bool,
    is_deputy: PropTypes.bool,
    everplan: PropTypes.object
  }),
  loadingCurrentUser: PropTypes.bool,
  params: PropTypes.object,
  readEndpoint: PropTypes.func,
  router: PropTypes.shape({
    isActive: PropTypes.func,
    listenBefore: PropTypes.func
  })
}

export default Nav
